/**
 * Occlusion visibility — the portable equivalent of taman-ml's
 * `intersect/open3d.py: calculate_plane_light_connection_batch` +
 * `dataloading/lighting_opt_task.py: _filter_visible_points`.
 *
 * taman-ml casts a ray from each luminaire toward each candidate measurement
 * point through the WHOLE scene (all meshes) and keeps the (light, point) pair
 * only when the first surface hit is at — or beyond — the point:
 *     visible  ⟺  t_hit ≥ |point − light| − EPSILON          (EPSILON = 1e-5)
 * If any surface sits between the lamp and the point, the light cannot reach it,
 * so that contribution is dropped. A point with NO luminaire able to see it is
 * thrown out entirely — which is why taman-ml reports fewer points than a pure
 * apex-distance gate would.
 *
 * Measurement points are lifted 0.01 m above their surface (grid.ts), exactly
 * like taman-ml, so a point never occludes itself: the ray from the lamp passes
 * through the point and only meets the point's own surface just BEYOND it.
 *
 * A median-split BVH over the scene triangles keeps this O(points·lights·logT)
 * instead of O(points·lights·T) — essential since the ray set is cast against
 * every triangle in the model.
 */
import type { SurfaceGeometry, Vec3 } from './types';

/** Matches taman-ml `intersect/open3d.py` EPSILON. */
const OCCLUSION_EPSILON = 1e-5;
const LEAF_SIZE = 4;
const RAY_EPS = 1e-9;

export interface OcclusionScene {
  /** 9 floats per triangle: v0xyz v1xyz v2xyz, ordered by `triIndex`-agnostic id. */
  verts: Float64Array;
  /** BVH-ordered triangle ids. */
  triIndex: Int32Array;
  nodeMin: Float64Array; // 3 per node
  nodeMax: Float64Array; // 3 per node
  /** internal node → left child index; leaf → first slot into triIndex. */
  leftFirst: Int32Array;
  /** 0 → internal node; >0 → leaf triangle count. */
  triCnt: Int32Array;
  nodeCount: number;
  triangles: number;
}

const collectTriangles = (geometries: SurfaceGeometry[]): Float64Array => {
  let total = 0;
  for (const g of geometries) {
    total += g.index ? g.index.length / 3 : g.positions.length / 9;
  }
  const verts = new Float64Array(total * 9);
  let o = 0;
  for (const g of geometries) {
    const { positions, index } = g;
    const triCount = index ? index.length / 3 : positions.length / 9;
    for (let t = 0; t < triCount; t += 1) {
      const i0 = index ? index[t * 3] : t * 3;
      const i1 = index ? index[t * 3 + 1] : t * 3 + 1;
      const i2 = index ? index[t * 3 + 2] : t * 3 + 2;
      verts[o] = positions[i0 * 3];
      verts[o + 1] = positions[i0 * 3 + 1];
      verts[o + 2] = positions[i0 * 3 + 2];
      verts[o + 3] = positions[i1 * 3];
      verts[o + 4] = positions[i1 * 3 + 1];
      verts[o + 5] = positions[i1 * 3 + 2];
      verts[o + 6] = positions[i2 * 3];
      verts[o + 7] = positions[i2 * 3 + 1];
      verts[o + 8] = positions[i2 * 3 + 2];
      o += 9;
    }
  }
  return verts;
};

/** Build a median-split BVH over every triangle in the supplied geometries. */
export const buildOcclusionScene = (geometries: SurfaceGeometry[]): OcclusionScene | null => {
  const verts = collectTriangles(geometries);
  const triangles = verts.length / 9;
  if (triangles === 0) return null;

  const triIndex = new Int32Array(triangles);
  const cx = new Float64Array(triangles);
  const cy = new Float64Array(triangles);
  const cz = new Float64Array(triangles);
  for (let i = 0; i < triangles; i += 1) {
    triIndex[i] = i;
    const b = i * 9;
    cx[i] = (verts[b] + verts[b + 3] + verts[b + 6]) / 3;
    cy[i] = (verts[b + 1] + verts[b + 4] + verts[b + 7]) / 3;
    cz[i] = (verts[b + 2] + verts[b + 5] + verts[b + 8]) / 3;
  }

  // Upper bound on node count for a binary BVH with the given leaf size.
  const maxNodes = Math.max(1, 2 * Math.ceil(triangles / LEAF_SIZE) + 1) * 2;
  const nodeMin = new Float64Array(maxNodes * 3);
  const nodeMax = new Float64Array(maxNodes * 3);
  const leftFirst = new Int32Array(maxNodes);
  const triCnt = new Int32Array(maxNodes);
  let nodeCount = 0;

  const computeBounds = (node: number, first: number, count: number): void => {
    let mnx = Infinity;
    let mny = Infinity;
    let mnz = Infinity;
    let mxx = -Infinity;
    let mxy = -Infinity;
    let mxz = -Infinity;
    for (let s = first; s < first + count; s += 1) {
      const b = triIndex[s] * 9;
      for (let v = 0; v < 9; v += 3) {
        const x = verts[b + v];
        const y = verts[b + v + 1];
        const z = verts[b + v + 2];
        if (x < mnx) mnx = x;
        if (y < mny) mny = y;
        if (z < mnz) mnz = z;
        if (x > mxx) mxx = x;
        if (y > mxy) mxy = y;
        if (z > mxz) mxz = z;
      }
    }
    nodeMin[node * 3] = mnx;
    nodeMin[node * 3 + 1] = mny;
    nodeMin[node * 3 + 2] = mnz;
    nodeMax[node * 3] = mxx;
    nodeMax[node * 3 + 1] = mxy;
    nodeMax[node * 3 + 2] = mxz;
  };

  // Iterative subdivision using an explicit work stack of (node, first, count).
  const stack: number[] = [];
  const rootFirst = 0;
  const root = nodeCount++;
  computeBounds(root, rootFirst, triangles);
  stack.push(root, rootFirst, triangles);

  while (stack.length) {
    const count = stack.pop()!;
    const first = stack.pop()!;
    const node = stack.pop()!;

    if (count <= LEAF_SIZE) {
      leftFirst[node] = first;
      triCnt[node] = count;
      continue;
    }

    // Split on the longest centroid-extent axis at its midpoint.
    const cArr = [cx, cy, cz];
    let mn0 = Infinity;
    let mn1 = Infinity;
    let mn2 = Infinity;
    let mx0 = -Infinity;
    let mx1 = -Infinity;
    let mx2 = -Infinity;
    for (let s = first; s < first + count; s += 1) {
      const ti = triIndex[s];
      if (cx[ti] < mn0) mn0 = cx[ti];
      if (cx[ti] > mx0) mx0 = cx[ti];
      if (cy[ti] < mn1) mn1 = cy[ti];
      if (cy[ti] > mx1) mx1 = cy[ti];
      if (cz[ti] < mn2) mn2 = cz[ti];
      if (cz[ti] > mx2) mx2 = cz[ti];
    }
    const ext = [mx0 - mn0, mx1 - mn1, mx2 - mn2];
    let axis = 0;
    if (ext[1] > ext[axis]) axis = 1;
    if (ext[2] > ext[axis]) axis = 2;
    const split = ((axis === 0 ? mn0 : axis === 1 ? mn1 : mn2) + (axis === 0 ? mx0 : axis === 1 ? mx1 : mx2)) / 2;
    const key = cArr[axis];

    // In-place partition triIndex[first, first+count) by centroid < split.
    let i = first;
    let j = first + count - 1;
    while (i <= j) {
      if (key[triIndex[i]] < split) {
        i += 1;
      } else {
        const tmp = triIndex[i];
        triIndex[i] = triIndex[j];
        triIndex[j] = tmp;
        j -= 1;
      }
    }
    let leftCount = i - first;
    if (leftCount === 0 || leftCount === count) {
      // Degenerate split (all centroids coincide on this axis) → split in half.
      leftCount = count >> 1;
    }

    const left = nodeCount++;
    const right = nodeCount++;
    computeBounds(left, first, leftCount);
    computeBounds(right, first + leftCount, count - leftCount);
    leftFirst[node] = left;
    triCnt[node] = 0; // internal
    stack.push(left, first, leftCount);
    stack.push(right, first + leftCount, count - leftCount);
  }

  return { verts, triIndex, nodeMin, nodeMax, leftFirst, triCnt, nodeCount, triangles };
};

/** Slab test: does ray (origin,dir) enter the AABB within [0, maxT]? */
const rayHitsAABB = (
  ox: number,
  oy: number,
  oz: number,
  idx: number,
  idy: number,
  idz: number,
  nMin: Float64Array,
  nMax: Float64Array,
  node: number,
  maxT: number,
): boolean => {
  const b = node * 3;
  let tmin = 0;
  let tmax = maxT;
  let t1 = (nMin[b] - ox) * idx;
  let t2 = (nMax[b] - ox) * idx;
  if (t1 > t2) {
    const t = t1;
    t1 = t2;
    t2 = t;
  }
  if (t1 > tmin) tmin = t1;
  if (t2 < tmax) tmax = t2;
  t1 = (nMin[b + 1] - oy) * idy;
  t2 = (nMax[b + 1] - oy) * idy;
  if (t1 > t2) {
    const t = t1;
    t1 = t2;
    t2 = t;
  }
  if (t1 > tmin) tmin = t1;
  if (t2 < tmax) tmax = t2;
  t1 = (nMin[b + 2] - oz) * idz;
  t2 = (nMax[b + 2] - oz) * idz;
  if (t1 > t2) {
    const t = t1;
    t1 = t2;
    t2 = t;
  }
  if (t1 > tmin) tmin = t1;
  if (t2 < tmax) tmax = t2;
  return tmax >= tmin;
};

/**
 * Is there ANY triangle blocking the segment from `origin` toward `dir` strictly
 * before `maxT`? Möller–Trumbore per triangle, BVH-accelerated, early-out on the
 * first blocker.
 */
// Reusable BVH-traversal stack. The engine casts occlusion rays one at a time on
// a single (worker) thread, so a module-level scratch buffer avoids allocating a
// fresh array per ray (hundreds of thousands of casts on a large scene). Grown on
// demand; a BVH of depth d needs at most ~2d slots.
let travStack = new Int32Array(64);

const anyOccluder = (
  scene: OcclusionScene,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxT: number,
): boolean => {
  const { verts, triIndex, nodeMin, nodeMax, leftFirst, triCnt } = scene;
  const idx = 1 / (dx || 1e-30);
  const idy = 1 / (dy || 1e-30);
  const idz = 1 / (dz || 1e-30);

  let sp = 0;
  travStack[sp++] = 0;
  while (sp > 0) {
    const node = travStack[--sp];
    if (!rayHitsAABB(ox, oy, oz, idx, idy, idz, nodeMin, nodeMax, node, maxT)) continue;

    const count = triCnt[node];
    if (count === 0) {
      if (sp + 2 > travStack.length) {
        const grown = new Int32Array(travStack.length * 2);
        grown.set(travStack);
        travStack = grown;
      }
      travStack[sp++] = leftFirst[node];
      travStack[sp++] = leftFirst[node] + 1;
      continue;
    }

    const first = leftFirst[node];
    for (let s = first; s < first + count; s += 1) {
      const b = triIndex[s] * 9;
      const v0x = verts[b];
      const v0y = verts[b + 1];
      const v0z = verts[b + 2];
      const e1x = verts[b + 3] - v0x;
      const e1y = verts[b + 4] - v0y;
      const e1z = verts[b + 5] - v0z;
      const e2x = verts[b + 6] - v0x;
      const e2y = verts[b + 7] - v0y;
      const e2z = verts[b + 8] - v0z;
      // p = dir × e2
      const px = dy * e2z - dz * e2y;
      const py = dz * e2x - dx * e2z;
      const pz = dx * e2y - dy * e2x;
      const det = e1x * px + e1y * py + e1z * pz;
      if (det > -1e-12 && det < 1e-12) continue; // ray parallel to triangle
      const inv = 1 / det;
      const tx = ox - v0x;
      const ty = oy - v0y;
      const tz = oz - v0z;
      const u = (tx * px + ty * py + tz * pz) * inv;
      if (u < 0 || u > 1) continue;
      const qx = ty * e1z - tz * e1y;
      const qy = tz * e1x - tx * e1z;
      const qz = tx * e1y - ty * e1x;
      const v = (dx * qx + dy * qy + dz * qz) * inv;
      if (v < 0 || u + v > 1) continue;
      const t = (e2x * qx + e2y * qy + e2z * qz) * inv;
      if (t <= RAY_EPS || t >= maxT) continue;
      // Open3D RaycastingScene only registers front-face hits. Skip backfaces so
      // grazing rays at sidewalk/curb edges are not falsely blocked.
      const nx = e1y * e2z - e1z * e2y;
      const ny = e1z * e2x - e1x * e2z;
      const nz = e1x * e2y - e1y * e2x;
      if (dx * nx + dy * ny + dz * nz >= 0) continue;
      return true;
    }
  }
  return false;
};

/** Nearest surface hit along a ray, with the geometric normal of the hit triangle. */
interface RayHit {
  /** Distance along the (assumed unit-length) ray direction. */
  t: number;
  /** Unit geometric normal of the hit triangle (not oriented). */
  nx: number;
  ny: number;
  nz: number;
}

/**
 * Closest triangle hit by ray (origin, dir) within (RAY_EPS, maxT]. `dir` MUST be
 * unit length so `t` is a world distance. Used to DRAPE measurement-grid points onto
 * the real model surface (so points follow uneven terrain instead of a flat plane).
 * Returns null when the ray misses every triangle. BVH-accelerated, full traversal
 * (no early-out — we need the nearest, not just any, hit).
 */
export const closestHit = (
  scene: OcclusionScene,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxT: number,
): RayHit | null => {
  const { verts, triIndex, nodeMin, nodeMax, leftFirst, triCnt } = scene;
  const idx = 1 / (dx || 1e-30);
  const idy = 1 / (dy || 1e-30);
  const idz = 1 / (dz || 1e-30);

  let bestT = maxT;
  let bestTri = -1;

  let sp = 0;
  travStack[sp++] = 0;
  while (sp > 0) {
    const node = travStack[--sp];
    if (!rayHitsAABB(ox, oy, oz, idx, idy, idz, nodeMin, nodeMax, node, bestT)) continue;

    const count = triCnt[node];
    if (count === 0) {
      if (sp + 2 > travStack.length) {
        const grown = new Int32Array(travStack.length * 2);
        grown.set(travStack);
        travStack = grown;
      }
      travStack[sp++] = leftFirst[node];
      travStack[sp++] = leftFirst[node] + 1;
      continue;
    }

    const first = leftFirst[node];
    for (let s = first; s < first + count; s += 1) {
      const b = triIndex[s] * 9;
      const v0x = verts[b];
      const v0y = verts[b + 1];
      const v0z = verts[b + 2];
      const e1x = verts[b + 3] - v0x;
      const e1y = verts[b + 4] - v0y;
      const e1z = verts[b + 5] - v0z;
      const e2x = verts[b + 6] - v0x;
      const e2y = verts[b + 7] - v0y;
      const e2z = verts[b + 8] - v0z;
      const px = dy * e2z - dz * e2y;
      const py = dz * e2x - dx * e2z;
      const pz = dx * e2y - dy * e2x;
      const det = e1x * px + e1y * py + e1z * pz;
      if (det > -1e-12 && det < 1e-12) continue;
      const inv = 1 / det;
      const tx = ox - v0x;
      const ty = oy - v0y;
      const tz = oz - v0z;
      const u = (tx * px + ty * py + tz * pz) * inv;
      if (u < 0 || u > 1) continue;
      const qx = ty * e1z - tz * e1y;
      const qy = tz * e1x - tx * e1z;
      const qz = tx * e1y - ty * e1x;
      const v = (dx * qx + dy * qy + dz * qz) * inv;
      if (v < 0 || u + v > 1) continue;
      const t = (e2x * qx + e2y * qy + e2z * qz) * inv;
      if (t > RAY_EPS && t < bestT) {
        bestT = t;
        bestTri = triIndex[s];
      }
    }
  }

  if (bestTri < 0) return null;
  const b = bestTri * 9;
  const e1x = verts[b + 3] - verts[b];
  const e1y = verts[b + 4] - verts[b + 1];
  const e1z = verts[b + 5] - verts[b + 2];
  const e2x = verts[b + 6] - verts[b];
  const e2y = verts[b + 7] - verts[b + 1];
  const e2z = verts[b + 8] - verts[b + 2];
  let nx = e1y * e2z - e1z * e2y;
  let ny = e1z * e2x - e1x * e2z;
  let nz = e1x * e2y - e1y * e2x;
  const len = Math.hypot(nx, ny, nz) || 1;
  nx /= len;
  ny /= len;
  nz /= len;
  return { t: bestT, nx, ny, nz };
};

/**
 * True if `point` is visible from `lightPos` (no surface blocks the segment) —
 * mirrors taman-ml's `t_hit ≥ dist − EPSILON` test by checking for any occluder
 * strictly nearer than `dist − EPSILON`.
 */
export const isVisibleFromLight = (scene: OcclusionScene, lightPos: Vec3, point: Vec3): boolean => {
  const dx = point[0] - lightPos[0];
  const dy = point[1] - lightPos[1];
  const dz = point[2] - lightPos[2];
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dist <= OCCLUSION_EPSILON) return true;
  const inv = 1 / dist;
  return !anyOccluder(
    scene,
    lightPos[0],
    lightPos[1],
    lightPos[2],
    dx * inv,
    dy * inv,
    dz * inv,
    dist - OCCLUSION_EPSILON,
  );
};
